import React, { useState } from 'react';
import { opsApi } from '../../../api/dbOps';
import { errorText } from '../../../api/migrationRunner';
import { Alert, ErrorBox, Panel, Pill, Spinner, duration } from '../runner/ui';
import { ServerPicker, num, sel, td, th, usePoll } from './shared';

/**
 * Slow queries (approved mockup "Database redesign", Slow queries): the
 * statement shapes that cost each server the most, from performance_schema.
 * Index suggestions are for a person to review; nothing is created.
 */
const SlowTab: React.FC = () => {
  const [server, setServer] = useState('');
  const [sort, setSort] = useState<'avg' | 'total' | 'count' | 'examined'>('avg');
  const servers = usePoll(() => opsApi.servers(), null);
  const slow = usePoll(() => opsApi.slow({ server: server || undefined, sort }), null, [server, sort]);
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (sql: string) => {
    try { await navigator.clipboard.writeText(sql); setCopied(sql); } catch { /* ignore */ }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <ServerPicker servers={servers.data || []} value={server} onChange={setServer} />
        <select className={sel} value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} aria-label="Sort by">
          <option value="avg">Slowest on average</option>
          <option value="total">Most time in total</option>
          <option value="count">Run most often</option>
          <option value="examined">Most rows read</option>
        </select>
        {slow.loading && <Spinner />}
      </div>
      {slow.error && !slow.data ? <ErrorBox>{errorText(slow.error)}</ErrorBox> : null}
      {(slow.data || []).map((s) => (
        <Panel key={s.server.key} title={s.server.label} bodyClass="p-0">
          {!s.ok ? <div className="p-4"><ErrorBox>{s.error}</ErrorBox></div>
            : !s.enabled ? (
              <div className="p-4">
                <Alert tone="info" title="Slow query data isn't enabled on this server">
                  <span className="block">{s.reason}.</span>
                  <span className="mt-1 block text-[12.5px]">{s.help}</span>
                </Alert>
              </div>
            ) : s.queries.length === 0 ? <div className="p-4 text-[13px] text-slate-500">No statements recorded yet.</div> : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr><th className={th}>Query (shape)</th><th className={th}>Avg</th><th className={th}>Times</th><th className={th}>Total</th><th className={th}>Database</th><th className={th}>Index</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {s.queries.map((q) => (
                      <tr key={`${q.db}:${q.digest}`}>
                        <td className={`${td} max-w-[520px]`}><code className="line-clamp-3 break-all font-mono text-[12px]" title={q.query || ''}>{q.query}</code></td>
                        <td className={`${td} whitespace-nowrap tabular-nums`}>{duration(q.avg_ms)}</td>
                        <td className={`${td} tabular-nums`}>{num(q.count)}</td>
                        <td className={`${td} whitespace-nowrap tabular-nums`}>{duration(q.total_ms)}</td>
                        <td className={td}>{q.company?.name || q.db}</td>
                        <td className={td}>
                          {q.suggestion ? (
                            <button type="button" className="text-left" onClick={() => copy(q.suggestion!.sql)} title={`${q.suggestion.why} Click to copy the SQL.`}>
                              <Pill tone="info">add index ({q.suggestion.columns.join(', ')})</Pill>
                              {copied === q.suggestion.sql && <span className="ml-1 text-[11px] text-slate-500">copied</span>}
                            </button>
                          ) : q.no_index_used > 0 ? <Pill tone="warn">no index used</Pill> : <Pill tone="ok">uses an index</Pill>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </Panel>
      ))}
      <p className="text-[12px] text-slate-500">Counts are since the server last started. Suggested indexes are a starting point: put the one you want in a migration file so every company gets it.</p>
    </div>
  );
};

export default SlowTab;
